import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors - auto logout on token expiry (do NOT logout on 403 permission errors)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear storage and redirect to login
      localStorage.removeItem('token');
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (userData) => api.post('/auth/register', userData);
export const getMe = () => api.get('/auth/me');
export const refreshToken = () => api.post('/auth/refresh');

// Companies
export const getCompanies = () => api.get('/companies');
export const getCompany = (id) => api.get(`/companies/${id}`);
export const createCompany = (data) => api.post('/companies', data);

// Assets
export const getAssets = (companyId, status, opts = {}) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  if (opts.include_archived) params.include_archived = true;
  if (opts.only_archived) params.only_archived = true;
  return api.get('/assets', { params });
};
export const getAsset = (id) => api.get(`/assets/${id}`);
export const createAsset = (data) => api.post('/assets', data);
export const updateAsset = (id, data) => api.patch(`/assets/${id}`, data);
export const updateAssetStatus = (id, status) => api.patch(`/assets/${id}/status?status=${status}`);
export const archiveAsset = (id) => api.post(`/assets/${id}/archive`);
export const unarchiveAsset = (id) => api.post(`/assets/${id}/unarchive`);
export const deleteAsset = (id) => api.delete(`/assets/${id}`);

// Drivers Master (reusable driver profiles for DN items)
export const listDrivers = (search) => api.get('/drivers/list', { params: search ? { search } : {} });
export const listExpiringDrivers = (days) => api.get('/drivers/expiring', { params: days ? { days } : {} });
export const upsertDriver = (data) => api.post('/drivers/upsert', data);
export const deleteDriver = (id) => api.delete(`/drivers/${id}`);

// DN item inspection checklist
export const saveItemInspection = (dnId, itemId, inspection) =>
  api.post(`/delivery-notes-v2/${dnId}/items/${itemId}/inspection`, inspection);
export const getAssetLastInspection = (assetId) =>
  api.get(`/delivery-notes-v2/asset/${assetId}/last-inspection`);

// Suppliers
export const getSuppliers = (companyId, status, search) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  if (search) params.search = search;
  return api.get('/suppliers', { params });
};
export const getSupplier = (id) => api.get(`/suppliers/${id}`);
export const createSupplier = (data) => api.post('/suppliers', data);
export const updateSupplier = (id, data) => api.put(`/suppliers/${id}`, data);
export const updateSupplierStatus = (id, status) => api.patch(`/suppliers/${id}/status?status=${status}`);
export const deleteSupplier = (id) => api.delete(`/suppliers/${id}`);
export const rateSupplier = (id, rating) => api.post(`/suppliers/${id}/rate?rating=${rating}`);

// Purchase Orders
export const getPurchaseOrders = (companyId, supplierId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (supplierId) params.supplier_id = supplierId;
  if (status) params.status = status;
  return api.get('/purchase-orders', { params });
};
export const getPurchaseOrder = (id) => api.get(`/purchase-orders/${id}`);
export const createPurchaseOrder = (data) => api.post('/purchase-orders', data);
export const approvePurchaseOrder = (id) => api.patch(`/purchase-orders/${id}/approve`);
export const sendPurchaseOrder = (id) => api.patch(`/purchase-orders/${id}/send`);
export const confirmPurchaseOrder = (id) => api.patch(`/purchase-orders/${id}/confirm`);
export const completePurchaseOrder = (id) => api.patch(`/purchase-orders/${id}/complete`);
export const downloadPurchaseOrderPDF = (id) => api.get(`/purchase-orders/${id}/pdf`, { responseType: 'blob' });

// Received Client POs
export const uploadReceivedPO = (formData) => api.post('/received-pos/upload', formData, {
  headers: { 'Content-Type': 'multipart/form-data' }
});
export const getReceivedPOs = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/received-pos', { params });
};
export const getReceivedPO = (id) => api.get(`/received-pos/${id}`);
export const getReceivedPODocument = (id) => api.get(`/received-pos/${id}/document`);

// Delivery Notes
export const getDeliveryNotes = (companyId, contractId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (contractId) params.contract_id = contractId;
  if (status) params.status = status;
  return api.get('/delivery-notes', { params });
};
export const getDeliveryNote = (id) => api.get(`/delivery-notes/${id}`);
export const createDeliveryNote = (data) => api.post('/delivery-notes', data);
export const issueDeliveryNote = (id) => api.patch(`/delivery-notes/${id}/issue`);
export const markDelivered = (id) => api.patch(`/delivery-notes/${id}/deliver`);
export const acknowledgeDelivery = (id, acknowledgedBy) => api.patch(`/delivery-notes/${id}/acknowledge?acknowledged_by=${acknowledgedBy}`);

// Enquiries
export const getEnquiries = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/enquiries', { params });
};
export const getEnquiry = (id) => api.get(`/enquiries/${id}`);
export const createEnquiry = (data) => api.post('/enquiries', data);
export const updateEnquiry = (id, data) => api.put(`/enquiries/${id}`, data);
export const updateEnquiryStatus = (id, status) => api.patch(`/enquiries/${id}/status?status=${status}`);

// Quotations
export const getQuotations = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/quotations', { params });
};
export const getQuotation = (id) => api.get(`/quotations/${id}`);
export const createQuotation = (data) => api.post('/quotations', data);
export const updateQuotation = (id, data) => api.put(`/quotations/${id}`, data);
export const approveQuotation = (id, notes) => api.patch(`/quotations/${id}/approve`, null, { params: { notes } });
export const sendQuotation = (id) => api.patch(`/quotations/${id}/send`);
export const reviseQuotation = (id) => api.post(`/quotations/${id}/revise`);
export const acceptQuotation = (id) => api.patch(`/quotations/${id}/accept`);
export const rejectQuotation = (id, reason) => api.patch(`/quotations/${id}/reject`, null, { params: { reason } });
export const declineQuotation = (id) => api.patch(`/quotations/${id}/reject`); // Legacy alias
export const convertQuotationToContract = (id, startDate) => api.post(`/quotations/${id}/convert-to-contract?start_date=${startDate}`);

// Create Client PO from Accepted Quotation
export const createClientPOFromQuotation = (quotationId, data = {}) => api.post(`/client-po/create-from-quotation/${quotationId}`, data);

// Contracts
export const getContracts = (companyId, status, search) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  if (search) params.search = search;
  return api.get('/contracts', { params });
};
export const getContract = (id) => api.get(`/contracts/${id}`);
export const createContract = (data) => api.post('/contracts', data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data);
export const updatePO = (id, poNumber) => api.patch(`/contracts/${id}/po?po_number=${poNumber}`);
export const mobilizeContract = (id, assetIds) => api.patch(`/contracts/${id}/mobilize?asset_ids=${assetIds.join(',')}`);
export const uploadClientPO = (id, fileUrl) => api.patch(`/contracts/${id}/client-po`, { file_url: fileUrl });

// Timesheets
export const getTimesheets = (contractId, month) => {
  const params = {};
  if (contractId) params.contract_id = contractId;
  if (month) params.month = month;
  return api.get('/timesheets', { params });
};
export const createTimesheet = (data) => api.post('/timesheets', data);
export const approveTimesheet = (id) => api.patch(`/timesheets/${id}/approve`);

// Invoices
export const getInvoices = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/invoices', { params });
};
export const createInvoice = (data) => api.post('/invoices', data);
export const approveInvoice = (id) => api.patch(`/invoices/${id}/approve`);
export const sendInvoice = (id) => api.patch(`/invoices/${id}/send`);
export const recordPayment = (id, amount) => api.patch(`/invoices/${id}/payment?payment_amount=${amount}`);

// Dashboard
export const getDashboardStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/dashboard/stats', { params });
};

// Reports
export const getAgingReport = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/reports/aging', { params });
};

// Commissions (legacy user-based)
export const getUserCommissions = (userId) => {
  const params = {};
  if (userId) params.user_id = userId;
  return api.get('/commissions', { params });
};

// Documents
export const getDocuments = (referenceType, referenceId) => {
  const params = {};
  if (referenceType) params.reference_type = referenceType;
  if (referenceId) params.reference_id = referenceId;
  return api.get('/documents', { params });
};
export const createDocument = (data) => api.post('/documents', data);

// File Upload
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Finance
export const getFinanceSummary = (period) => {
  const params = {};
  if (period) params.period = period;
  return api.get('/finance/summary', { params });
};
export const getProfitLoss = (period) => {
  const params = {};
  if (period) params.period = period;
  return api.get('/finance/profit-loss', { params });
};
// Old getBalanceSheet and getTrialBalance moved to Phase 4 reports
export const getTrialBalance = () => api.get('/finance/trial-balance');

// Email Parser
export const parseEmail = (data) => api.post('/email-parser/parse', data);
export const testEmailParse = (data) => api.post('/email-parser/test-parse', data);
export const getGmailAuthUrl = () => api.get('/email-parser/gmail/auth-url');
export const processGmailEmails = (data) => api.post('/email-parser/gmail/process', data);
export const getEmailParseJobStatus = (jobId) => api.get(`/email-parser/jobs/${jobId}`);
export const getEmailSourcedEnquiries = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/email-parser/enquiries/email-sourced', { params });
};

// ==================== FINANCE CORE (IPO-Ready) ====================

// Chart of Accounts
export const getChartOfAccounts = (companyId, accountType, includeBalances = false) => {
  const params = { company_id: companyId, include_balances: includeBalances };
  if (accountType) params.account_type = accountType;
  return api.get('/finance/accounts', { params });
};
export const getAccount = (accountId) => api.get(`/finance/accounts/${accountId}`);
export const createAccount = (data) => api.post('/finance/accounts', data);
export const updateAccount = (accountId, data) => api.put(`/finance/accounts/${accountId}`, data);
export const seedDefaultAccounts = (companyId) => api.post(`/finance/accounts/seed-defaults?company_id=${companyId}`);

// Accounting Periods
export const getAccountingPeriods = (companyId, fiscalYear, status) => {
  const params = { company_id: companyId };
  if (fiscalYear) params.fiscal_year = fiscalYear;
  if (status) params.status = status;
  return api.get('/finance/periods', { params });
};
export const createAccountingPeriod = (data) => api.post('/finance/periods', data);
export const closeAccountingPeriod = (periodId, closeType, reason) => 
  api.patch(`/finance/periods/${periodId}/close?close_type=${closeType}${reason ? '&reason=' + encodeURIComponent(reason) : ''}`);
export const reopenAccountingPeriod = (periodId, reason) => 
  api.patch(`/finance/periods/${periodId}/reopen?reason=${encodeURIComponent(reason)}`);

// Journal Entries
export const getJournalEntries = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/journal-entries', { params });
};
export const getJournalEntry = (entryId) => api.get(`/finance/journal-entries/${entryId}`);
export const createJournalEntry = (data) => api.post('/finance/journal-entries', data);
export const approveJournalEntry = (entryId) => api.patch(`/finance/journal-entries/${entryId}/approve`);
export const postJournalEntry = (entryId) => api.patch(`/finance/journal-entries/${entryId}/post`);
export const reverseJournalEntry = (entryId, reason, reversalDate) => 
  api.post(`/finance/journal-entries/${entryId}/reverse?reason=${encodeURIComponent(reason)}&reversal_date=${reversalDate}`);

// Day Book
export const getDayBook = (companyId, date, includeDrafts = false) => {
  const params = { company_id: companyId, date, include_drafts: includeDrafts };
  return api.get('/finance/day-book', { params });
};
export const getDayBookRange = (companyId, fromDate, toDate) => {
  const params = { company_id: companyId, from_date: fromDate, to_date: toDate };
  return api.get('/finance/day-book/range', { params });
};

// General Ledger
export const getGeneralLedger = (companyId, accountId, fromDate, toDate) => {
  const params = { company_id: companyId, account_id: accountId };
  if (fromDate) params.from_date = fromDate;
  if (toDate) params.to_date = toDate;
  return api.get('/finance/general-ledger', { params });
};

// Trial Balance
export const getTrialBalanceReport = (companyId, asOfDate) => {
  const params = { company_id: companyId, as_of_date: asOfDate };
  return api.get('/finance/trial-balance', { params });
};

// Audit Log
export const getAuditLog = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/audit-log', { params });
};

// ==================== FIXED ASSETS ====================
export const getFixedAssets = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/assets/fixed-assets', { params });
};
export const getFixedAsset = (assetId) => api.get(`/finance/assets/fixed-assets/${assetId}`);
export const createFixedAsset = (data) => api.post('/finance/assets/fixed-assets', data);
export const updateFixedAsset = (assetId, data) => api.put(`/finance/assets/fixed-assets/${assetId}`, data);
export const disposeFixedAsset = (assetId, data) => api.post(`/finance/assets/fixed-assets/${assetId}/dispose`, data);

// Depreciation
export const calculateDepreciation = (data) => api.post('/finance/assets/depreciation/calculate', data);
export const postDepreciation = (data) => api.post('/finance/assets/depreciation/post', data);
export const getDepreciationSchedule = (assetId) => api.get(`/finance/assets/depreciation/schedule?asset_id=${assetId}`);

// Loans
export const getLoans = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/assets/loans', { params });
};
export const createLoan = (data) => api.post('/finance/assets/loans', data);
export const getLoanSchedule = (loanId) => api.get(`/finance/assets/loans/${loanId}/schedule`);
export const recordEmiPayment = (loanId, data) => api.post(`/finance/assets/loans/${loanId}/record-payment`, data);
export const getBankExposure = (companyId) => api.get(`/finance/assets/bank-exposure?company_id=${companyId}`);

// ==================== AUTO-POSTING ====================
export const autoPostInvoice = (data) => api.post('/finance/auto-post/invoice', data);
export const autoPostPaymentReceipt = (data) => api.post('/finance/auto-post/payment-receipt', data);
export const autoPostSupplierInvoice = (data) => api.post('/finance/auto-post/supplier-invoice', data);
export const autoPostSupplierPayment = (data) => api.post('/finance/auto-post/supplier-payment', data);

// ==================== INVOICE PDF GENERATOR ====================
export const generateInvoicePDF = (data) => api.post('/invoices/generate-pdf', data);
export const generateInvoicePDFDownload = (data) => api.post('/invoices/generate-pdf-download', data, { responseType: 'blob' });
export const getInvoicePDF = (invoiceId) => api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
export const previewInvoice = (data) => api.post('/invoices/preview', data);

// ==================== REVENUE & COST (Phase 3) ====================

// Revenue Accrual
export const calculateRevenueAccrual = (data) => api.post('/finance/revenue/accrual/calculate', data);
export const postRevenueAccrual = (data) => api.post('/finance/revenue/accrual/post', data);
export const getAccrualHistory = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/revenue/accrual/history', { params });
};

// Deferred Revenue
export const createDeferredRevenue = (data) => api.post('/finance/revenue/deferred/create', data);
export const recognizeDeferredRevenue = (data) => api.post('/finance/revenue/deferred/recognize', data);
export const getDeferredRevenue = (companyId, status) => {
  const params = { company_id: companyId };
  if (status) params.status = status;
  return api.get('/finance/revenue/deferred', { params });
};

// Cost Tracking
export const recordCost = (data) => api.post('/finance/revenue/costs', data);
export const getCosts = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/revenue/costs', { params });
};
export const getCostSummary = (companyId, contractId, period) => {
  const params = { company_id: companyId };
  if (contractId) params.contract_id = contractId;
  if (period) params.period = period;
  return api.get('/finance/revenue/costs/summary', { params });
};

// Margin Analysis
export const getContractMargin = (contractId) => api.get(`/finance/revenue/margin/contract/${contractId}`);
export const getMarginSummary = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/revenue/margin/summary', { params });
};

// Timesheet-based Invoicing
export const generateInvoiceFromTimesheet = (data) => api.post('/finance/revenue/invoice-from-timesheet', data);

// ==================== FINANCIAL REPORTS (Phase 4) ====================

// Financial Statements
export const getProfitLossStatement = (companyId, periodFrom, periodTo, comparative = false) => {
  const params = { company_id: companyId, period_from: periodFrom, period_to: periodTo, comparative };
  return api.get('/finance/reports/profit-loss', { params });
};
export const getBalanceSheet = (companyId, asOfDate) => {
  const params = { company_id: companyId, as_of_date: asOfDate };
  return api.get('/finance/reports/balance-sheet', { params });
};
export const getCashFlowStatement = (companyId, periodFrom, periodTo) => {
  const params = { company_id: companyId, period_from: periodFrom, period_to: periodTo };
  return api.get('/finance/reports/cash-flow', { params });
};

// Ratio Analysis
export const getFinancialRatios = (companyId, asOfDate) => {
  const params = { company_id: companyId, as_of_date: asOfDate };
  return api.get('/finance/reports/ratios', { params });
};

// Credit/Debit Notes
export const createCreditNote = (data) => api.post('/finance/reports/credit-notes', data);
export const createDebitNote = (data) => api.post('/finance/reports/debit-notes', data);
export const getCreditNotes = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/reports/credit-notes', { params });
};
export const getDebitNotes = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/reports/debit-notes', { params });
};
export const approveCreditNote = (noteId) => api.patch(`/finance/reports/credit-notes/${noteId}/approve`);

// VAT Reconciliation
export const getVatReconciliation = (companyId, period) => {
  const params = { company_id: companyId, period };
  return api.get('/finance/reports/vat-reconciliation', { params });
};

// Executive Dashboard
export const getFinancialDashboard = (companyId) => {
  const params = { company_id: companyId };
  return api.get('/finance/reports/dashboard', { params });
};

// Trend Data
export const getFinancialTrends = (companyId, months = 12) => {
  const params = { company_id: companyId, months };
  return api.get('/finance/reports/trends', { params });
};

// Multi-Company Consolidation
export const getConsolidatedDashboard = () => api.get('/finance/reports/consolidated/dashboard');
export const getConsolidatedPnL = (periodFrom, periodTo) => {
  const params = { period_from: periodFrom, period_to: periodTo };
  return api.get('/finance/reports/consolidated/profit-loss', { params });
};

// Payroll
export const recordPayroll = (data) => api.post('/finance/reports/payroll/record', data);
export const getPayroll = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/reports/payroll', { params });
};
export const postPayrollToGL = (payrollId) => api.post(`/finance/reports/payroll/${payrollId}/post`);

// Commissions
export const recordCommission = (data) => api.post('/finance/reports/commissions/record', data);
export const getCommissions = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/reports/commissions', { params });
};
export const getCommissionSummary = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/reports/commissions/summary', { params });
};

// ==================== PHASE 6: GOVERNANCE & EXPORT ====================

// Inter-Company Transactions
export const createInterCompanyTransaction = (data) => api.post('/finance/governance/inter-company/create', data);
export const approveInterCompanyTransaction = (icId) => api.patch(`/finance/governance/inter-company/${icId}/approve`);
export const getInterCompanyTransactions = (companyId, filters = {}) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  Object.assign(params, filters);
  return api.get('/finance/governance/inter-company', { params });
};
export const getInterCompanyBalances = () => api.get('/finance/governance/inter-company/balances');

// Budgets
export const createBudget = (data) => api.post('/finance/governance/budgets', data);
export const getBudgets = (companyId, filters = {}) => {
  const params = { company_id: companyId, ...filters };
  return api.get('/finance/governance/budgets', { params });
};
export const approveBudget = (budgetId) => api.patch(`/finance/governance/budgets/${budgetId}/approve`);
export const getBudgetVsActual = (companyId, fiscalYear, period) => {
  const params = { company_id: companyId, fiscal_year: fiscalYear };
  if (period) params.period = period;
  return api.get('/finance/governance/budget-vs-actual', { params });
};
export const getFinancialForecast = (companyId, monthsAhead = 6) => {
  const params = { company_id: companyId, months_ahead: monthsAhead };
  return api.get('/finance/governance/forecast', { params });
};

// Export Functions
export const exportProfitLoss = (companyId, periodFrom, periodTo, format = 'excel') => {
  const params = { company_id: companyId, period_from: periodFrom, period_to: periodTo, format };
  return api.get('/finance/governance/export/profit-loss', { params, responseType: 'blob' });
};
export const exportBalanceSheet = (companyId, asOfDate, format = 'excel') => {
  const params = { company_id: companyId, as_of_date: asOfDate, format };
  return api.get('/finance/governance/export/balance-sheet', { params, responseType: 'blob' });
};
export const exportTrends = (companyId, months = 12, format = 'excel') => {
  const params = { company_id: companyId, months, format };
  return api.get('/finance/governance/export/trends', { params, responseType: 'blob' });
};

// Audit Trail
export const getAuditTrail = (filters = {}) => api.get('/finance/governance/audit-trail', { params: filters });
export const getEntityAuditHistory = (entityType, entityId) => 
  api.get(`/finance/governance/audit-trail/entity/${entityType}/${entityId}`);
export const getPendingApprovals = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/finance/governance/pending-approvals', { params });
};

// ==================== BANK RECONCILIATION ====================
export const uploadBankStatement = (formData) => 
  api.post('/finance/bank/statements/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const getBankStatements = (companyId, status) => {
  const params = { company_id: companyId };
  if (status) params.status = status;
  return api.get('/finance/bank/statements', { params });
};
export const getBankStatementDetail = (statementId) => 
  api.get(`/finance/bank/statements/${statementId}`);
export const autoMatchTransactions = (statementId) => 
  api.post(`/finance/bank/statements/${statementId}/auto-match`);
export const manualMatchTransaction = (statementId, transactionId, data) => 
  api.patch(`/finance/bank/statements/${statementId}/transactions/${transactionId}/match`, data);
export const ignoreTransaction = (statementId, transactionId, reason) => 
  api.patch(`/finance/bank/statements/${statementId}/transactions/${transactionId}/ignore`, { reason });
export const getReconciliationSummary = (companyId) => 
  api.get('/finance/bank/reconciliation-summary', { params: { company_id: companyId } });

// Audit Export
export const exportAuditTrail = (filters = {}) => 
  api.get('/finance/governance/audit-trail/export', { params: filters, responseType: 'blob' });

// ==================== EPIC 0: CLIENT PURCHASE ORDERS ====================

// Client PO CRUD
export const createClientPO = (data) => api.post('/client-po/create', data);
export const getClientPOs = (filters = {}) => api.get('/client-po/list', { params: filters });
export const getClientPO = (poId) => api.get(`/client-po/${poId}`);
export const updateClientPO = (poId, data) => api.patch(`/client-po/${poId}`, data);
export const cancelClientPO = (poId) => api.delete(`/client-po/${poId}`);
export const uploadClientPODocument = async (poId, file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post(`/client-po/${poId}/upload-document`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};
export const getClientPODocument = (poId) => api.get(`/client-po/${poId}/document`);
export const canCreateDelivery = (poId) => api.get(`/client-po/${poId}/can-create-delivery`);
export const getClientPOStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/client-po/stats/summary', { params });
};

// Delivery Notes V2 (Epic 0)
export const createDeliveryNoteV2 = (data) => api.post('/delivery-notes-v2/create', data);
export const getDeliveryNotesV2 = (filters = {}) => api.get('/delivery-notes-v2/list', { params: filters });
export const getDeliveryNoteV2 = (dnId) => api.get(`/delivery-notes-v2/${dnId}`);
export const updateDeliveryNoteV2 = (dnId, data) => api.patch(`/delivery-notes-v2/${dnId}`, data);
export const markDNDelivered = (dnId, data = {}) => api.patch(`/delivery-notes-v2/${dnId}/deliver`, data);
export const acknowledgeDNDelivery = (dnId, data) => api.patch(`/delivery-notes-v2/${dnId}/acknowledge`, data);
export const getDeliveryNotePDF = (dnId) => api.get(`/delivery-notes-v2/${dnId}/pdf`, { responseType: 'blob' });
export const getSignedDeliveryPDF = (dnId) => api.get(`/delivery-notes-v2/${dnId}/signed-pdf`, { responseType: 'blob' });
export const getDNStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/delivery-notes-v2/stats/summary', { params });
};

// POD (Proof of Delivery) Photos
export const uploadPODPhoto = (dnId, file, photoType = 'delivery', notes = '', gps = null, itemId = null) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams({ photo_type: photoType, notes });
  if (itemId) params.set('item_id', itemId);
  if (gps && gps.latitude != null && gps.longitude != null) {
    params.set('latitude', String(gps.latitude));
    params.set('longitude', String(gps.longitude));
    if (gps.accuracy_m != null) params.set('accuracy_m', String(gps.accuracy_m));
    if (gps.captured_at) params.set('captured_at', gps.captured_at);
  }
  return api.post(`/delivery-notes-v2/${dnId}/pod/upload?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const uploadMultiplePODPhotos = (dnId, files, photoType = 'delivery') => {
  const formData = new FormData();
  files.forEach(file => formData.append('files', file));
  return api.post(`/delivery-notes-v2/${dnId}/pod/upload-multiple?photo_type=${photoType}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

export const getPODPhotos = (dnId) => api.get(`/delivery-notes-v2/${dnId}/pod/photos`);
export const getPODPhoto = (dnId, photoId, asBase64 = true) => 
  api.get(`/delivery-notes-v2/${dnId}/pod/photo/${photoId}?as_base64=${asBase64}`);
export const deletePODPhoto = (dnId, photoId) => api.delete(`/delivery-notes-v2/${dnId}/pod/photo/${photoId}`);
export const updatePODPhoto = (dnId, photoId, data) => api.patch(`/delivery-notes-v2/${dnId}/pod/photo/${photoId}`, data);

// Mobile-optimized DN endpoints
export const getPendingDeliveriesMobile = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/delivery-notes-v2/mobile/pending-delivery', { params });
};
export const getDeliveryNoteMobile = (dnId) => api.get(`/delivery-notes-v2/mobile/${dnId}`);
export const quickDeliverMobile = (dnId, data) => api.post(`/delivery-notes-v2/mobile/${dnId}/quick-deliver`, data);

// Material Invoice (Epic 3)
export const createMaterialInvoice = (dnId) => api.post(`/delivery-notes-v2/${dnId}/create-invoice`);
export const getMaterialInvoicePending = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/delivery-notes-v2/invoice-pending/list', { params });
};

// Timesheets V2 (Epic 0)
export const getTimesheetReminders = (filters = {}) => api.get('/timesheets-v2/reminders', { params: filters });
export const markReminderReceived = (reminderId) => api.patch(`/timesheets-v2/reminders/${reminderId}/received`);
export const cancelReminder = (reminderId, reason) => api.patch(`/timesheets-v2/reminders/${reminderId}/cancel`, { reason });
export const createClientTimesheet = (data) => api.post('/timesheets-v2/client/create', data);
export const getClientTimesheets = (filters = {}) => api.get('/timesheets-v2/client/list', { params: filters });
export const getClientTimesheetDetail = (tsId) => api.get(`/timesheets-v2/client/${tsId}`);
export const approveClientTimesheet = (tsId) => api.patch(`/timesheets-v2/client/${tsId}/approve`);
export const createSupplierTimesheet = (data) => api.post('/timesheets-v2/supplier/create', data);
export const getSupplierTimesheets = (filters = {}) => api.get('/timesheets-v2/supplier/list', { params: filters });
export const approveSupplierTimesheet = (tsId) => api.patch(`/timesheets-v2/supplier/${tsId}/approve`);
export const getTimesheetStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/timesheets-v2/stats/summary', { params });
};

// Clients Master Data
export const getClients = (companyId, search) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (search) params.search = search;
  return api.get('/clients', { params });
};
export const getClient = (clientId) => api.get(`/clients/${clientId}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (clientId, data) => api.put(`/clients/${clientId}`, data);
export const deleteClient = (clientId) => api.delete(`/clients/${clientId}`);

// ==================== WORKFLOW & APPROVALS (P1) ====================

// Fleet Availability Check
export const checkFleetAvailability = (companyId, assetType, quantity) => {
  const params = { company_id: companyId, asset_type: assetType, quantity };
  return api.get('/workflow/check-fleet-availability', { params });
};

// Purchase Requests (for supplier-sourced equipment)
export const createPurchaseRequest = (data) => api.post('/workflow/purchase-request', data);
export const getPurchaseRequests = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/workflow/purchase-requests', { params });
};
export const provideSupplierRates = (prId, data) => api.patch(`/workflow/purchase-request/${prId}/provide-rates`, data);
export const createQuotationFromEnquiry = (enquiryId, data) => api.post(`/workflow/quotation-from-enquiry/${enquiryId}`, data);

// Quotation Approval Workflow
export const submitQuotationForApproval = (quotationId) => api.post(`/workflow/quotation/${quotationId}/submit-for-approval`);
export const getPendingApprovalQuotations = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/workflow/pending-approvals', { params });
};
export const approveQuotationWorkflow = (quotationId, data) => api.patch(`/workflow/quotation/${quotationId}/approve`, data);
export const rejectQuotationInternal = (quotationId, data) => api.patch(`/workflow/quotation/${quotationId}/reject-internal`, data);
export const recordClientRejection = (quotationId, data) => api.patch(`/workflow/quotation/${quotationId}/client-rejection`, data);

// Quotation Reminders & Escalation
export const getQuotationReminders = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/workflow/quotation-reminders', { params });
};
export const escalateQuotation = (quotationId) => api.post(`/workflow/escalate-quotation/${quotationId}`);

// Minimum Rates (Admin)
export const getMinimumRates = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/workflow/minimum-rates', { params });
};
export const setMinimumRate = (data) => api.post('/workflow/minimum-rates', data);

// Workflow Stats
export const getWorkflowStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/workflow/stats', { params });
};

// ==================== RBAC & DATA SCOPING (P2) ====================

// User Info for Pre-filling
export const getMyInfo = () => api.get('/rbac/my-info');

// Scoped Data (filtered by user role)
export const getMyScopedEnquiries = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/rbac/my-enquiries', { params });
};

export const getMyScopedQuotations = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/rbac/my-quotations', { params });
};

export const getMyScopedDeliveryNotes = (companyId, status) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  return api.get('/rbac/my-delivery-notes', { params });
};

export const getMyScopedClientPOs = (companyId, status, hasDocument) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  if (status) params.status = status;
  if (hasDocument !== undefined) params.has_document = hasDocument;
  return api.get('/rbac/my-client-pos', { params });
};

// Fleet Visibility
export const getAvailableFleet = (companyId, assetType) => {
  const params = { company_id: companyId };
  if (assetType) params.asset_type = assetType;
  return api.get('/rbac/available-fleet', { params });
};

export const getMyMobilisedFleet = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/rbac/my-mobilised-fleet', { params });
};

// Sales Notifications
export const getMyNotifications = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/rbac/my-notifications', { params });
};

// Permission Check
export const checkPermission = (action) => api.get('/rbac/check-permission', { params: { action } });

// User Stats (scoped to current user)
export const getMyStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/rbac/my-stats', { params });
};

// ==================== AI/OCR MODULE ====================

// Multi-Vehicle Timesheet OCR Extraction (NEW)
export const extractMultiVehicleTimesheet = (file, clientName, clientPoId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (clientName) formData.append('client_name', clientName);
  if (clientPoId) formData.append('client_po_id', clientPoId);
  return api.post('/ocr/extract-timesheet-multi', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Get Vehicles from Extraction
export const getExtractionVehicles = (extractionId) => api.get(`/ocr/extraction/${extractionId}/vehicles`);

// Map Vehicle to Supplier
export const mapVehicleToSupplier = (vehicleNumber, supplierId) => 
  api.post('/ocr/map-vehicle-supplier', null, { params: { vehicle_number: vehicleNumber, supplier_id: supplierId } });

// Get Vehicle-Supplier Mappings
export const getVehicleSupplierMappings = () => api.get('/ocr/vehicle-supplier-mappings');

// Create Supplier Timesheets from Extraction
export const createSupplierTimesheets = (extractionId, companyId, vehicleSupplierMap) => 
  api.post(`/ocr/create-supplier-timesheets/${extractionId}?company_id=${companyId}`, vehicleSupplierMap);

// List Supplier Timesheets
export const listSupplierTimesheets = (params) => api.get('/ocr/supplier-timesheets', { params });

// Get Supplier Timesheet
export const getSupplierTimesheet = (timesheetId) => api.get(`/ocr/supplier-timesheet/${timesheetId}`);

// Send Supplier Timesheet
export const sendSupplierTimesheet = (timesheetId) => api.post(`/ocr/send-supplier-timesheet/${timesheetId}`);

// Timesheet OCR Extraction (Single Vehicle - Legacy)
export const extractTimesheetData = (file, deliveryNoteId, clientPoId) => {
  const formData = new FormData();
  formData.append('file', file);
  if (deliveryNoteId) formData.append('delivery_note_id', deliveryNoteId);
  if (clientPoId) formData.append('client_po_id', clientPoId);
  return api.post('/ocr/extract-timesheet', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Client PO OCR Extraction
export const extractClientPOData = (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/ocr/extract-client-po', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
};

// Confirm OCR Extraction
export const confirmOCRExtraction = (extractionId, confirmedData) => 
  api.post(`/ocr/confirm-extraction/${extractionId}`, confirmedData);

// Create Timesheet from OCR Extraction
export const createTimesheetFromExtraction = (extractionId, companyId, deliveryNoteId, clientPoId) => {
  const params = new URLSearchParams();
  params.append('company_id', companyId);
  if (deliveryNoteId) params.append('delivery_note_id', deliveryNoteId);
  if (clientPoId) params.append('client_po_id', clientPoId);
  return api.post(`/ocr/create-timesheet-from-extraction/${extractionId}?${params.toString()}`);
};

// Create Client PO from OCR Extraction
export const createClientPOFromExtraction = (extractionId, companyId, clientId) => {
  const params = new URLSearchParams();
  params.append('company_id', companyId);
  if (clientId) params.append('client_id', clientId);
  return api.post(`/ocr/create-client-po-from-extraction/${extractionId}?${params.toString()}`);
};

// Get OCR Extraction
export const getOCRExtraction = (extractionId) => api.get(`/ocr/extraction/${extractionId}`);

// List OCR Extractions
export const listOCRExtractions = (params) => api.get('/ocr/extractions', { params });

// ==================== INVESTOR POOLS MODULE ====================

// Investors
export const listInvestors = (params) => api.get('/investor-pools/investors', { params });
export const getInvestor = (investorId) => api.get(`/investor-pools/investors/${investorId}`);
export const createInvestor = (data) => api.post('/investor-pools/investors', data);
export const updateInvestor = (investorId, data) => api.patch(`/investor-pools/investors/${investorId}`, data);
export const deleteInvestor = (investorId) => api.delete(`/investor-pools/investors/${investorId}`);
export const getInvestorDashboard = (investorId) => api.get(`/investor-pools/investors/${investorId}/dashboard`);

// Pools
export const listInvestorPools = (params) => api.get('/investor-pools/pools', { params });
export const getInvestorPool = (poolId) => api.get(`/investor-pools/pools/${poolId}`);
export const createInvestorPool = (data) => api.post('/investor-pools/pools', data);
export const updateInvestorPool = (poolId, data) => api.patch(`/investor-pools/pools/${poolId}`, data);
export const deleteInvestorPool = (poolId) => api.delete(`/investor-pools/pools/${poolId}`);

// Pool Rules
export const getPoolRules = (poolId) => api.get(`/investor-pools/pools/${poolId}/rules`);
export const updatePoolRules = (poolId, data) => api.patch(`/investor-pools/pools/${poolId}/rules`, data);

// Pool Assets
export const listPoolAssets = (poolId) => api.get(`/investor-pools/pools/${poolId}/assets`);
export const addAssetToPool = (poolId, data) => api.post(`/investor-pools/pools/${poolId}/assets`, data);
export const removeAssetFromPool = (poolId, poolAssetId) => api.delete(`/investor-pools/pools/${poolId}/assets/${poolAssetId}`);

// Pool Investments
export const listPoolInvestments = (poolId) => api.get(`/investor-pools/pools/${poolId}/investments`);
export const addPoolInvestment = (poolId, data) => api.post(`/investor-pools/pools/${poolId}/investments`, data);
export const updatePoolInvestment = (poolId, investmentId, data) => api.patch(`/investor-pools/pools/${poolId}/investments/${investmentId}`, data);

// Stats
export const getInvestorPoolStats = () => api.get('/investor-pools/stats/summary');

// ==================== POOL FINANCIAL TRACKING ====================

// Revenue Ledger
export const listPoolRevenue = (poolId, params) => api.get(`/investor-pools/finance/pools/${poolId}/revenue`, { params });
export const addPoolRevenue = (poolId, data) => api.post(`/investor-pools/finance/pools/${poolId}/revenue`, data);
export const updateRevenueEntry = (poolId, entryId, params) => api.patch(`/investor-pools/finance/pools/${poolId}/revenue/${entryId}`, null, { params });

// Expense Ledger
export const listPoolExpenses = (poolId, params) => api.get(`/investor-pools/finance/pools/${poolId}/expenses`, { params });
export const addPoolExpense = (poolId, data) => api.post(`/investor-pools/finance/pools/${poolId}/expenses`, data);
export const deletePoolExpense = (poolId, entryId) => api.delete(`/investor-pools/finance/pools/${poolId}/expenses/${entryId}`);

// Payout Ledger
export const listPoolPayouts = (poolId, params) => api.get(`/investor-pools/finance/pools/${poolId}/payouts`, { params });
export const createPoolPayout = (poolId, data) => api.post(`/investor-pools/finance/pools/${poolId}/payouts`, data);
export const approvePoolPayout = (poolId, payoutId) => api.patch(`/investor-pools/finance/pools/${poolId}/payouts/${payoutId}/approve`);

// Financial Summary & ROI
export const getPoolFinancialSummary = (poolId) => api.get(`/investor-pools/finance/pools/${poolId}/summary`);
export const calculateDistribution = (poolId, amount) => api.post(`/investor-pools/finance/pools/${poolId}/calculate-distribution`, null, { params: { amount_to_distribute: amount } });
export const generatePayouts = (poolId, amount, period) => api.post(`/investor-pools/finance/pools/${poolId}/generate-payouts`, null, { params: { amount_to_distribute: amount, period } });

// Auto-link invoice
export const autoLinkInvoiceToPool = (invoiceId) => api.post('/investor-pools/finance/auto-link-invoice', null, { params: { invoice_id: invoiceId } });

// ==================== INVESTOR PORTAL (Investor-facing) ====================

// Create investor-specific axios instance for portal endpoints
const investorApi = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add investor token to requests
investorApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('investor_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors for investor - redirect to investor login
investorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('investor_token');
      localStorage.removeItem('investor');
      if (window.location.pathname.startsWith('/investor')) {
        window.location.href = '/investor-login';
      }
    }
    return Promise.reject(error);
  }
);

// Investor Authentication
export const investorLogin = (username, password) => 
  api.post('/investor-pools/auth/login', { username, password });

export const getInvestorProfile = () => investorApi.get('/investor-pools/auth/me');

// Investor Portal Dashboard
export const getInvestorDashboardSummary = () => investorApi.get('/investor-pools/portal/dashboard-summary');
export const getInvestorMyPools = () => investorApi.get('/investor-pools/portal/my-pools');
export const getInvestorPoolDetails = (poolId) => investorApi.get(`/investor-pools/portal/pool/${poolId}/details`);

// ==================== DEMOBILIZATION / OFF-HIRE ====================

// Demob CRUD
export const createDemobRequest = (data) => api.post('/demobilization/create', data);
export const getDemobList = (filters = {}) => api.get('/demobilization/list', { params: filters });
export const getDemobDetail = (demobId) => api.get(`/demobilization/${demobId}`);
export const scheduleDemob = (demobId, data) => api.patch(`/demobilization/${demobId}/schedule`, data);
export const startDemobInspection = (demobId) => api.patch(`/demobilization/${demobId}/start-inspection`);
export const cancelDemob = (demobId, reason) => api.patch(`/demobilization/${demobId}/cancel`, { reason });

// Demob Inspection
export const addDemobInspection = (demobId, data) => api.post(`/demobilization/${demobId}/inspection`, data);
export const getDemobInspections = (demobId) => api.get(`/demobilization/${demobId}/inspections`);
export const updateDemobInspection = (demobId, inspectionId, data) => 
  api.patch(`/demobilization/${demobId}/inspection/${inspectionId}`, data);

// Demob Damages
export const addDemobDamage = (demobId, data) => api.post(`/demobilization/${demobId}/damage`, data);
export const getDemobDamages = (demobId) => api.get(`/demobilization/${demobId}/damages`);
export const updateDemobDamage = (demobId, damageId, data) => 
  api.patch(`/demobilization/${demobId}/damage/${damageId}`, data);
export const deleteDemobDamage = (demobId, damageId) =>
  api.delete(`/demobilization/${demobId}/damage/${damageId}`);
export const approveDemobDamage = (demobId, damageId, remarks) =>
  api.post(`/demobilization/${demobId}/damage/${damageId}/approve`, { remarks });
export const rejectDemobDamage = (demobId, damageId, remarks) =>
  api.post(`/demobilization/${demobId}/damage/${damageId}/reject`, { remarks });
export const setDemobDamageBillable = (demobId, damageId, payload) =>
  api.post(`/demobilization/${demobId}/damage/${damageId}/set-billable`, payload);
export const deleteDemobPhoto = (demobId, photoId) =>
  api.delete(`/demobilization/${demobId}/photo/${photoId}`);
export const getDemobReadiness = (demobId) => api.get(`/demobilization/${demobId}/readiness`);

// Demob Photos
export const uploadDemobPhoto = (demobId, file, photoType = 'return_condition', inspectionItemId = null, damageId = null, caption = '', onUploadProgress = null) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = new URLSearchParams({ photo_type: photoType });
  if (inspectionItemId) params.append('inspection_item_id', inspectionItemId);
  if (damageId) params.append('damage_id', damageId);
  if (caption) params.append('caption', caption);
  return api.post(`/demobilization/${demobId}/photo?${params.toString()}`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: onUploadProgress || undefined,
  });
};
export const getDemobPhotos = (demobId) => api.get(`/demobilization/${demobId}/photos`);
export const getDemobPhoto = (demobId, photoId) => api.get(`/demobilization/${demobId}/photo/${photoId}`);

// Demob Completion
export const completeDemobInspection = (demobId, data) => api.patch(`/demobilization/${demobId}/complete-inspection`, data);
export const generateDemobFinalInvoice = (demobId, includeDamages = true) => 
  api.post(`/demobilization/${demobId}/generate-final-invoice?include_damages=${includeDamages}`);
export const completeDemob = (demobId) => api.patch(`/demobilization/${demobId}/complete`);

// Demob Stats
export const getDemobStats = (companyId) => {
  const params = {};
  if (companyId) params.company_id = companyId;
  return api.get('/demobilization/stats/summary', { params });
};

// Demob audit trail (read-only, immutable)
export const getDemobAudit = (demobId) => api.get(`/demobilization/${demobId}/audit`);

// Operations Invoices (V2 - auto-generated from timesheets/material/transport)
export const getInvoicesV2 = (params = {}) => api.get('/invoices/v2/list', { params });
export const approveInvoiceV2 = (id) => api.patch(`/invoices/v2/${id}/approve`);
export const sendInvoiceV2 = (id) => api.patch(`/invoices/v2/${id}/send`);

// Risk Mode PO Follow-ups
export const getPOFollowups = (params = {}) => api.get('/delivery-notes-v2/po-followups/list', { params });
export const markDNPOReceived = (dnId, data) => api.patch(`/delivery-notes-v2/${dnId}/po-received`, data);
export const runPOFollowupsNow = () => api.post('/delivery-notes-v2/followups/run-now');

export default api;
